import { css } from '@emotion/react';
import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { TriStateCheckboxComponent } from '~/_shared/baseComponents/checkbox';
import {
  PermanentConfirmStrategy, useConfirmationModal,
} from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import type { Theme } from '~/_shared/themes/theme.model';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIsDataExportAllowed } from '~/_shared/types/toolsAndFeatures/useIsDataExportAllowed';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { getBoundaryGroupLowestZoomLevel } from '~/boundary/boundary.helpers';
import {
  BoundaryTerritoryListingExportLimit, BoundaryTerritoryListingLimit,
} from '~/boundary/constants';
import { useLoadMoreListing } from '~/boundary/hooks/useLoadMoreListing';
import { useModal } from '~/modal/useModal.hook';
import { ActiveBoundaryComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundary.component';
import { ActiveBoundaryOptimizedTrialToolbarComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundaryOptimizedTrialToolbar.component';
import { ActiveBoundaryToolbarComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundaryToolbar.component';
import { BoundaryItemContainer } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryItem.container';
import { BoundaryRowLoadMoreComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryRowLoadMore.component';
import { BoundaryTerritoryContainer } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryTerritory.container';
import { useOptimizedTerritoriesFeatureBlocker } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/useOptimizedTerritoriesFeatureBlocker';
import { ZoomToLocationOrHiddenMessageComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/zoomToLocationOrHiddenMessage.component';
import { useBoundaryEdit } from '~/sidebar/sidebarApps/mapTools/boundary/edit/useBoundaryEdit';
import { useBoundaryTerritoryGroupDisplayName } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryTerritoryGroupDisplayName';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  checkBoundaryIsCustom, useBoundaryGroupPermissions, useGetBoundaryGroup,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import {
  useBoundaryTerritoryBoundsDetailsIsLoadingSelector, useBoundaryTerritoryDetailsSelector,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { boundaryTerritoryGroupsRemoveRequest } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  boundarySelectEditBoundaryTerritoryOpenModal, boundarySelectEditCloseModal,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import type { BoundarySelectState } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import { convertBoundariesShowNameModal } from '~/store/frontendState/mapTools/boundary/convertBoundaries/convertBoundaries.actionCreators';
import { mapSettingsBoundariesSetPrimaryBoundaryGroupId } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import { useMapSettingsBoundariesPrimaryBoundaryGroupIdSelector } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.selectors';
import { setBoundaryFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import { useBoundaryToolFilters } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.selectors';
import { useMapSettingsExportImageSettingsModeSelector } from '~/store/mapSettings/toolsState/exportImageSettings/exportImageSettings.selectors';
import { useIsMapEditDisabled } from '~/store/selectors/useMapInfoSelectors';
import type { SortedBTGs } from '../boundaryTools.types';
import { useBoundaryItems } from '../hooks/useBoundaryItems';
import { useBTGFillData } from '../hooks/useBTGFillData';
import {
  boundaryOptionsHeaderStyle, boundaryOptionStyle, checkboxStyle,
} from './styles';

type ActiveBoundaryContainerProps = {
  readonly boundaryGroupId: BoundaryGroupId;
  readonly boundaryTerritoryGroups: SortedBTGs;
  readonly legendMode?: boolean;
};

const trialTerritoriesNotLoadedMessageStyle = (theme: Theme) => css({
  fontSize: '14px',
  margin: 0,
  padding: 10,
  color: theme.textColors.secondary,
});

export const ActiveBoundaryContainer = ({
  boundaryGroupId,
  boundaryTerritoryGroups,
  legendMode,
}: ActiveBoundaryContainerProps) => {
  const sidebarBTGHighlightedId = useSelector(state => state.frontendState.mapTools.boundary.boundarySidebarHighlight.highlightedBoundaryTerritoryGroupId);
  const dispatch = useDispatch();
  const { openModal: openBoundaryCustomizeMetricsModal } = useModal('BoundaryCustomizeMetrics');
  const { openModal: openBoundaryCustomizeFillsModal } = useModal('BoundaryCustomizeFills');
  const { openModal: openCloneBoundaryTerritoryGroupModal } = useModal('CloneBoundaryTerritoryGroup');
  const { openModal: openBoundaryAddFillModal } = useModal('BoundaryAddFill');
  const { openModal: openBoundarySettingsModal } = useModal('BoundarySettings');
  const boundaryFilters = useBoundaryToolFilters();
  const loadedBoundaryDetails = useBoundaryTerritoryDetailsSelector();
  const isLoadingBoundsLimitedDetails = useBoundaryTerritoryBoundsDetailsIsLoadingSelector();
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const boundaryGroup = getBoundaryGroupById(boundaryGroupId);
  const isImageExport = notNullsy(useMapSettingsExportImageSettingsModeSelector());
  const primaryBoundaryGroupId = useMapSettingsBoundariesPrimaryBoundaryGroupIdSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const isBoundaryExportLocationsAllowed = useIsDataExportAllowed(MapTool.Boundary);
  const { startBoundaryEdit } = useBoundaryEdit();
  const { getBoundaryTerritoryGroupName } = useBoundaryTerritoryGroupDisplayName();
  const { openModal: openExportBoundaryTerritoryGroupDataModal } = useModal('ExportBoundaryTerritoryGroupData');
  const { openConfirmationModal, closeConfirmationModal } = useConfirmationModal();
  const { openModal: openBoundaryTerritoryEditSubmitModal } = useModal('BoundaryTerritoryEditSubmit');
  const { openModal: openUpgradeSubscriptionModal } = useModal('WMSUpgradeSubscription');
  const { openModal: openExportContainedBoundaryModal } = useModal('ExportContainedBoundaryData');
  const { openModal: openExportBoundaryTerritoryAssignmentsModal } = useModal('ExportBoundaryTerritoryAssignments');
  const [t] = useTranslation();

  const {
    allowFillChange,
    activeBoundaryTerritoryGroup,
    activeBTGOption,
    filteredBTGDropDownOptions,
    setActiveBTGId,
    isOnlyNoFillOption,
  } = useBTGFillData({
    boundaryGroupId,
    boundaryTerritoryGroups,
  });

  const targetZoomLevel = boundaryGroup && getBoundaryGroupLowestZoomLevel(boundaryGroup, activeBoundaryTerritoryGroup);
  const boundaryDetails = loadedBoundaryDetails.get(activeBoundaryTerritoryGroup.boundaryTerritoryGroupId);
  const fillRequiresZoomToLoadDetails = !boundaryDetails && !!targetZoomLevel;

  const {
    boundaryTerritoryList: boundaryTerritoryArray,
    displayBoundaryTerritories,
    areGroupBoundariesEmpty,
    sortedBoundaryList: sortedBoundaryArray,
  } = useBoundaryItems({ boundaryTerritoryGroup: activeBoundaryTerritoryGroup });

  const isBoundariesAsTerritories = !!boundaryTerritoryArray.length &&
    activeBoundaryTerritoryGroup.settings.boundariesAsTerritories;

  const {
    boundaryIsOptimizedTerritoriesTrial,
    blockWhenOptimizedTerritoriesTrial,
    remainingGeometryCalls,
  } = useOptimizedTerritoriesFeatureBlocker(activeBoundaryTerritoryGroup.boundaryGroupId);

  const initialRowLimit = legendMode ? BoundaryTerritoryListingExportLimit : BoundaryTerritoryListingLimit;
  const {
    limit: shownBoundaryKeySettingCountLimit,
    increaseLimit: increaseShownBoundaryKeySettingCountLimit,
  } = useLoadMoreListing(initialRowLimit);
  const {
    limit: shownBoundaryTerritoryKeySettingCountLimit,
    increaseLimit: increaseShownBoundaryTerritoryKeySettingCountLimit,
  } = useLoadMoreListing(initialRowLimit);

  const displayName = getBoundaryTerritoryGroupName(activeBoundaryTerritoryGroup);
  const boundaryPermissions = useBoundaryGroupPermissions(boundaryGroupId);
  const { boundaryIsCustom } = boundaryPermissions;

  const filter = boundaryFilters.get(boundaryGroupId);

  const removeBoundaryFromMap = useCallback(() => {
    const boundaryGroup = getBoundaryGroupById(boundaryGroupId);
    const isCustom = checkBoundaryIsCustom(boundaryGroup);
    const entity = isCustom ? t('territory group') : t('boundary');

    openConfirmationModal({
      title: t('Remove {{entity}} from map', { entity }),
      confirmCaption: t('Proceed'),
      text: t('You are about to remove a {{entity}} from the map. Do you want to proceed?',
        { entity }),
      permanentConfirmSettings: {
        id: 'remove-boundary-territory-group-from-map',
        strategy: PermanentConfirmStrategy.Session,
      },
      isConfirmButtonDestructive: true,
      onCancel: () => {
        closeConfirmationModal();
      },
      onConfirm: () => {
        dispatch(boundaryTerritoryGroupsRemoveRequest(boundaryTerritoryGroups.filter(notNullsy).map(btg => btg.boundaryTerritoryGroupId)));
        closeConfirmationModal();
      },
    });
  }, [boundaryGroupId, boundaryTerritoryGroups, closeConfirmationModal, dispatch, getBoundaryGroupById, openConfirmationModal, t]);

  const onToggleShowAll = useCallback(() => {
    dispatch(setBoundaryFilter(activeBoundaryTerritoryGroup.boundaryGroupId, {
      ...filter,
      filteredBoundaryTerritories: [],
      filteredBoundaries: [],
      showAll: filter.showAll === TriStateRange.Full
        ? TriStateRange.None
        : TriStateRange.Full,
    }));
  }, [activeBoundaryTerritoryGroup.boundaryGroupId, dispatch, filter]);

  const onClone = useCallback(() => openCloneBoundaryTerritoryGroupModal({
    boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
  }), [openCloneBoundaryTerritoryGroupModal, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId]);

  const handleSettingsClick = useCallback(() => {
    openBoundarySettingsModal({
      boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
    });
  }, [openBoundarySettingsModal, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId]);

  const handleAddFillClick = useMemo(() => blockWhenOptimizedTerritoriesTrial(() => {
    openBoundaryAddFillModal({
      boundaryGroupId: activeBoundaryTerritoryGroup.boundaryGroupId,
    });
  }), [activeBoundaryTerritoryGroup.boundaryGroupId, blockWhenOptimizedTerritoriesTrial, openBoundaryAddFillModal]);

  const handleCustomizeFillClick = useMemo(() => blockWhenOptimizedTerritoriesTrial(() => {
    openBoundaryCustomizeFillsModal({
      boundaryGroupId: activeBoundaryTerritoryGroup.boundaryGroupId,
    });
  }),
  [blockWhenOptimizedTerritoriesTrial, openBoundaryCustomizeFillsModal, activeBoundaryTerritoryGroup.boundaryGroupId]);

  const handleCustomizeMetricsClick = useCallback(() => {
    openBoundaryCustomizeMetricsModal({
      boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
      isBoundaryGroupCustom: boundaryIsCustom,
    });
  }, [openBoundaryCustomizeMetricsModal, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId, boundaryIsCustom]);

  const handleStartEditClick = useMemo(() => {
    if (boundaryIsCustom && !boundaryPermissions.boundaryIsEditable) {
      return undefined;
    }

    return blockWhenOptimizedTerritoriesTrial(() => {
      if (boundaryIsCustom) {
        startBoundaryEdit(activeBoundaryTerritoryGroup.boundaryTerritoryGroupId);
      }
      else {
        dispatch(boundarySelectEditBoundaryTerritoryOpenModal({
          selectBoundaryGroupId: activeBoundaryTerritoryGroup.boundaryGroupId,
          boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
          onClose: () => {
            dispatch(boundarySelectEditCloseModal());
          },
          onSubmit: (selectState: BoundarySelectState) => {
            if (!selectState?.selectedBoundaryInfos?.length) {
              return;
            }

            openBoundaryTerritoryEditSubmitModal({
              boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
              boundaryIds: selectState.selectedBoundaryInfos.map(boundaryInfo => boundaryInfo.boundaryId),
            });
          },
        }));
      }
    });
  },
  [boundaryIsCustom, boundaryPermissions.boundaryIsEditable, blockWhenOptimizedTerritoriesTrial, startBoundaryEdit,
    activeBoundaryTerritoryGroup.boundaryTerritoryGroupId, activeBoundaryTerritoryGroup.boundaryGroupId, dispatch,
    openBoundaryTerritoryEditSubmitModal]);

  const openExportContainedBoundariesModal = useCallback(() => {
    if (isBoundariesAsTerritories) {
      openExportContainedBoundaryModal({ filter: {
        type: 'boundary_territory',
        boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
        boundaryTerritoryIds: boundaryTerritoryArray.map(bt => bt.boundaryTerritoryId),
      } });
    }
    else {
      openExportContainedBoundaryModal({ filter: {
        type: 'boundary',
        boundaryGroupId: activeBoundaryTerritoryGroup.boundaryGroupId,
        boundaryIds: sortedBoundaryArray.map(b => b.id),
        matchings: activeBoundaryTerritoryGroup.matchings,
      } });
    }
  }, [isBoundariesAsTerritories, openExportContainedBoundaryModal, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId, activeBoundaryTerritoryGroup.boundaryGroupId, activeBoundaryTerritoryGroup.matchings, boundaryTerritoryArray, sortedBoundaryArray]);

  const handleToggleIsPrimary = useCallback(() => {
    dispatch(mapSettingsBoundariesSetPrimaryBoundaryGroupId(activeBoundaryTerritoryGroup.boundaryGroupId));
  }, [dispatch, activeBoundaryTerritoryGroup.boundaryGroupId]);

  const handleConvert = useCallback(() =>
    dispatch(convertBoundariesShowNameModal(activeBoundaryTerritoryGroup.boundaryTerritoryGroupId)),
  [activeBoundaryTerritoryGroup.boundaryTerritoryGroupId, dispatch]);

  const handleExportClick = useCallback(() =>
    openExportBoundaryTerritoryGroupDataModal({
      selectedBoundaryTerritoryGroupIds: [activeBoundaryTerritoryGroup.boundaryTerritoryGroupId],
      showSelectBoundaryTerritoryGroupsSection: false,
    }),
  [openExportBoundaryTerritoryGroupDataModal, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId],
  );

  const handleExportAssignmentsClick = useCallback(() => {
    openExportBoundaryTerritoryAssignmentsModal({
      boundaryTerritoryGroupId: activeBoundaryTerritoryGroup.boundaryTerritoryGroupId,
    });
  }, [openExportBoundaryTerritoryAssignmentsModal, activeBoundaryTerritoryGroup.boundaryTerritoryGroupId]);

  const containsLegend = sortedBoundaryArray.length || boundaryTerritoryArray.length;

  if (legendMode && !containsLegend) {
    return null;
  }

  const isBtgTrialAndEmpty = boundaryIsOptimizedTerritoriesTrial && remainingGeometryCalls === 0 && areGroupBoundariesEmpty;
  const allBoundariesHidden = filter.showAll === TriStateRange.None;

  const renderBuckets = () => {
    return (
      <>
        {!legendMode && !isBtgTrialAndEmpty && (
          <div css={boundaryOptionsHeaderStyle}>
            <div css={boundaryOptionStyle}>
              <TriStateCheckboxComponent
                checkmark={filter.showAll}
                checkedSetter={onToggleShowAll}
                text={boundaryIsCustom ? t('Show All Territories') : t('Show All Boundaries')}
                textStyle={checkboxStyle}
              />
            </div>
          </div>
        )}

        {boundaryDetails && displayBoundaryTerritories && (
          <>
            {boundaryTerritoryArray.map(
              (boundaryTerritory, index) => {
                return (index < shownBoundaryTerritoryKeySettingCountLimit) && (
                  <BoundaryTerritoryContainer
                    key={boundaryTerritory.boundaryTerritoryId}
                    boundaryTerritory={boundaryTerritory}
                    boundaryTerritoryGroup={activeBoundaryTerritoryGroup}
                    legendMode={legendMode}
                  />
                );
              },
            )}

            {(boundaryTerritoryArray.length > shownBoundaryTerritoryKeySettingCountLimit) && (
              <BoundaryRowLoadMoreComponent
                onButtonClick={increaseShownBoundaryTerritoryKeySettingCountLimit}
              />
            )}
          </>
        )}
      </>
    );
  };

  const toolbar = isBtgTrialAndEmpty ? (
    <ActiveBoundaryOptimizedTrialToolbarComponent
      boundaryPermissions={boundaryPermissions}
      onRemove={removeBoundaryFromMap}
      onPurchaseClick={openUpgradeSubscriptionModal}
    />
  ) : (
    <ActiveBoundaryToolbarComponent
      boundaryGroupId={activeBoundaryTerritoryGroup.boundaryGroupId}
      boundaryDisplayName={displayName}
      boundaryIsOptimizedTerritoriesTrial={boundaryIsOptimizedTerritoriesTrial}
      boundaryPermissions={boundaryPermissions}
      onAddFillClick={handleAddFillClick}
      onClone={!isEditModeDisabled && boundaryIsCustom ? onClone : undefined}
      onConvert={activeBoundaryTerritoryGroup.settings.boundaryTerritories.length ? handleConvert : undefined}
      onCustomizeFillClick={handleCustomizeFillClick}
      onCustomizeMetricsClick={handleCustomizeMetricsClick}
      onExportClick={isBoundaryExportLocationsAllowed ? handleExportClick : undefined}
      onExportAssignments={isBoundaryExportLocationsAllowed && !boundaryIsCustom ? handleExportAssignmentsClick : undefined} // custom BG's would be just the same value for each row in export, as they only support default fill
      onExportContainedBoundaries={(boundaryIsCustom || isBoundariesAsTerritories) ? openExportContainedBoundariesModal : undefined}
      onRemove={removeBoundaryFromMap}
      onSettingsClick={handleSettingsClick}
      onStartEditClick={handleStartEditClick}
    />
  );

  if (fillRequiresZoomToLoadDetails && isImageExport) {
    // No need to show a legend for a boundary group not shown on the map in image export
    return null;
  }

  return (
    <ActiveBoundaryComponent
      dropdownProps={{
        allowFillChange,
        activeBTGOption,
        filteredBTGDropDownOptions,
        setActiveBTGId,
        isOnlyNoFillOption,
      }}
      isHighlighted={activeBoundaryTerritoryGroup.boundaryGroupId === sidebarBTGHighlightedId}
      showActions={!legendMode}
      name={displayName}
      toolbar={isEditModeDisabled ? null : toolbar}
      boundaryIsOptimizedTerritoriesTrial={boundaryIsOptimizedTerritoriesTrial}
      isPrimary={activeBoundaryTerritoryGroup.boundaryGroupId === primaryBoundaryGroupId}
      onToggleIsPrimary={handleToggleIsPrimary}
    >

      {renderBuckets()}
      {fillRequiresZoomToLoadDetails ? (
        <ZoomToLocationOrHiddenMessageComponent
          allBoundariesHidden={allBoundariesHidden}
          targetZoomLevel={targetZoomLevel}
          isLoading={isLoadingBoundsLimitedDetails}
        />
      ) : null}

      {(!displayBoundaryTerritories && !areGroupBoundariesEmpty) && sortedBoundaryArray
        .map((boundary, index) => {
          if (index < shownBoundaryKeySettingCountLimit) {
            return (
              <BoundaryItemContainer
                boundary={boundary}
                boundaryPermissions={boundaryPermissions}
                boundaryTerritoryGroup={activeBoundaryTerritoryGroup}
                key={boundary.id}
                legendMode={legendMode}
              />
            );
          }

          return null;
        })}

      {isBtgTrialAndEmpty && (
        <p css={trialTerritoriesNotLoadedMessageStyle}>
          {t('auboundaryAI.BTGinTrialMode')}
        </p>
      )}

      {!displayBoundaryTerritories && (sortedBoundaryArray.length > shownBoundaryKeySettingCountLimit) && (
        <BoundaryRowLoadMoreComponent
          onButtonClick={increaseShownBoundaryKeySettingCountLimit}
        />
      )}

    </ActiveBoundaryComponent>
  );
};
